import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  ArrowPathIcon,
  BookOpenIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { AIGenerator } from "../../../utils/AIGenerator";
import { copyTextToClipboard } from "../../../utils/copyTextToClipboard";
import { aiGeneratorConfigs } from "../../../../config/aiGeneratorConfigs";
import { PROMANG_OFFER_MODULE_BUILDER_AI_GUIDE } from "./module-builder-ai-guide";
import {
  parseImportedModuleGraphJson,
  SAMPLE_MODULE_GRAPH_JSON,
  serializeModuleGraphForExport,
} from "./module-builder-import-utils";

/**
 * View/edit module_graph JSON + copy AI authoring instructions.
 * Apply updates Formik/canvas only — never auto-saves the calculation.
 */
export function ModuleBuilderJsonDialog({
  open,
  onClose,
  graph,
  aiContext = {},
  onApply,
  /** Above module settings dialog (4000) when opened from the builder chrome. */
  zIndex = 4100,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("json");
  const [draft, setDraft] = useState("");
  const [importMode, setImportMode] = useState("replace");
  const [parseError, setParseError] = useState("");
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      setDraft(serializeModuleGraphForExport(graph));
      setParseError("");
      setTab("json");
      setImportMode("replace");
    }
    prevOpen.current = open;
  }, [open, graph]);

  const handleCopyJson = async () => {
    try {
      await copyTextToClipboard(draft);
      toast.success(t("offer.calculation.module.json.copy_success"));
    } catch {
      toast.error(t("offer.calculation.module.json.clipboard_failed"));
    }
  };

  const handleCopyAiGuide = async () => {
    try {
      await copyTextToClipboard(PROMANG_OFFER_MODULE_BUILDER_AI_GUIDE);
      toast.success(t("offer.calculation.module.json.ai_guide_copied"));
    } catch {
      toast.error(t("offer.calculation.module.json.clipboard_failed"));
    }
  };

  const handleResetDraft = () => {
    setDraft(serializeModuleGraphForExport(graph));
    setParseError("");
    toast.success(t("offer.calculation.module.json.reload_success"));
  };

  const handleInsertSample = () => {
    setDraft(SAMPLE_MODULE_GRAPH_JSON);
    setParseError("");
  };

  const handleApply = () => {
    try {
      const parsed = parseImportedModuleGraphJson(draft);
      onApply?.({ graph: parsed, mode: importMode });
      toast.success(
        t("offer.calculation.module.json.applied", { count: parsed.nodes.length })
      );
      onClose();
    } catch (e) {
      const code = e?.code || "unknown";
      const key = `offer.calculation.module.json.error_${code}`;
      const msg = t(key);
      const display = msg !== key ? msg : e?.message || String(e);
      setParseError(display);
      toast.error(display);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="4xl"
      title={t("offer.calculation.module.json.dialog_title")}
      zIndex={zIndex}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          <button
            type="button"
            onClick={() => setTab("json")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "json" ? "bg-sky-100 text-sky-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <DocumentTextIcon className="h-5 w-5 shrink-0" />
            {t("offer.calculation.module.json.tab_json")}
          </button>
          <button
            type="button"
            onClick={() => setTab("ai")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "ai" ? "bg-slate-100 text-slate-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <BookOpenIcon className="h-5 w-5 shrink-0" />
            {t("offer.calculation.module.json.tab_ai_guide")}
          </button>
        </div>

        {tab === "json" ? (
          <>
            <p className="text-sm text-gray-600">{t("offer.calculation.module.json.hint")}</p>
            <textarea
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setParseError("");
              }}
              spellCheck={false}
              className="min-h-[min(420px,50vh)] w-full flex-1 rounded-lg border border-gray-300 font-mono text-xs leading-relaxed focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
            />
            {parseError ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{parseError}</p>
            ) : null}
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="moduleBuilderImportMode"
                  checked={importMode === "replace"}
                  onChange={() => setImportMode("replace")}
                />
                {t("offer.calculation.module.json.replace")}
              </label>
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="moduleBuilderImportMode"
                  checked={importMode === "append"}
                  onChange={() => setImportMode("append")}
                />
                {t("offer.calculation.module.json.append")}
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCopyJson}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
                {t("offer.calculation.module.json.copy_json")}
              </button>
              <button
                type="button"
                onClick={handleResetDraft}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-5 w-5" />
                {t("offer.calculation.module.json.reload_from_editor")}
              </button>
              <button
                type="button"
                onClick={handleInsertSample}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("offer.calculation.module.json.insert_sample")}
              </button>
              <AIGenerator
                module="offer_module_builder"
                config={aiGeneratorConfigs.offer_module_builder}
                context={aiContext}
                fieldsToGenerate={["module_graph_json"]}
                onGenerate={(generatedFields) => {
                  const raw = generatedFields?.module_graph_json;
                  if (raw == null || (typeof raw === "string" && !String(raw).trim())) {
                    toast.error(t("offer.calculation.module.json.ai_parse_error"));
                    return;
                  }
                  try {
                    const parsed = parseImportedModuleGraphJson(
                      typeof raw === "string" ? raw : JSON.stringify(raw)
                    );
                    setDraft(serializeModuleGraphForExport(parsed));
                    setParseError("");
                    toast.success(
                      t("offer.calculation.module.json.ai_apply_success", {
                        count: parsed.nodes.length,
                      })
                    );
                    setTab("json");
                  } catch {
                    toast.error(t("offer.calculation.module.json.ai_parse_error"));
                  }
                }}
                buttonLabel={t("offer.calculation.module.json.generate_with_ai")}
              />
              <button
                type="button"
                onClick={handleApply}
                className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
              >
                {t("offer.calculation.module.json.apply")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {t("offer.calculation.module.json.ai_guide_intro")}
            </p>
            <pre className="max-h-[min(420px,55vh)] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-left font-mono text-xs leading-relaxed text-slate-800">
              {PROMANG_OFFER_MODULE_BUILDER_AI_GUIDE}
            </pre>
            <button
              type="button"
              onClick={handleCopyAiGuide}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
              {t("offer.calculation.module.json.copy_ai_guide")}
            </button>
          </>
        )}
      </div>
    </PMDialog>
  );
}
